import { useState } from 'react';
import { useT } from '../i18n';
import {
  getNotificationPermission,
  getPrefs,
  requestBrowserPermission,
  setPrefs,
  type Prefs,
} from '../lib/notify';

// ANX-053 — блок управления нотификациями в SettingsPage:
//  - чекбоксы звук / браузер-уведомление (localStorage);
//  - запрос Notification.permission, если он ещё в default;
//  - подсказки для denied / unsupported.

export function NotificationSettings() {
  const t = useT();
  const [prefs, setPrefsState] = useState<Prefs>(() => getPrefs());
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>(
    () => getNotificationPermission(),
  );
  const [requesting, setRequesting] = useState(false);

  function toggle(key: keyof Prefs) {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    setPrefsState(next);
  }

  async function onRequest() {
    setRequesting(true);
    const res = await requestBrowserPermission();
    setPermission(res);
    setRequesting(false);
  }

  return (
    <div className="mt-4 rounded-md border border-slate-200 bg-white p-4 shadow-card">
      <h3 className="mb-3 text-xs text-slate-500">{t('notify.title')}</h3>
      <label className="mb-2 flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={prefs.sound}
          onChange={() => toggle('sound')}
          className="accent-accent-600"
        />
        <span>{t('notify.sound')}</span>
      </label>
      <label className="mb-2 flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={prefs.browser}
          onChange={() => toggle('browser')}
          className="accent-accent-600"
        />
        <span>{t('notify.browser')}</span>
      </label>
      <PermissionBlock
        permission={permission}
        requesting={requesting}
        onRequest={() => void onRequest()}
        t={t}
      />
    </div>
  );
}

function PermissionBlock({
  permission,
  requesting,
  onRequest,
  t,
}: {
  permission: NotificationPermission | 'unsupported';
  requesting: boolean;
  onRequest: () => void;
  t: ReturnType<typeof useT>;
}) {
  if (permission === 'unsupported') {
    return <p className="mt-2 text-xs text-slate-500">{t('notify.permissionUnsupported')}</p>;
  }
  if (permission === 'denied') {
    return <p className="mt-2 text-xs text-red-600">{t('notify.permissionDenied')}</p>;
  }
  if (permission === 'granted') {
    return <p className="mt-2 text-xs text-green-700">{t('notify.permissionGranted')}</p>;
  }
  return (
    <div className="mt-2 flex items-center gap-2">
      <button
        type="button"
        disabled={requesting}
        onClick={onRequest}
        className="rounded-md bg-accent-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-accent-700 disabled:cursor-not-allowed disabled:bg-slate-300"
      >
        {t('notify.requestPermission')}
      </button>
      <span className="text-xs text-slate-500">{t('notify.permissionDefault')}</span>
    </div>
  );
}
